import { useState, useCallback, useRef } from 'react';
import {
    AccountInfo,
    InteractionRequiredAuthError,
    PublicClientApplication,
    RedirectRequest,
    SilentRequest,
} from '@azure/msal-browser';
import { AxiosInstance } from 'axios';
import { TokenRefreshError } from '../errors/TokenRefreshError';
import { setAxiosHeader } from '../../HubLayout/util/authUtils';
import { sanitizeRedirectUrl } from '../util/urlValidation';

export interface UseAuthenticationResult {
    account: AccountInfo | undefined;
    error: Error | undefined;
    /** Attempt silent auth or redirect. Pass `true` to clear cached state and force a fresh login. */
    login: (forceNew?: boolean) => Promise<void>;
}

/**
 * Custom hook to handle MSAL authentication.
 */
export const useAuthentication = (
    msalInstance: PublicClientApplication,
    loginRequest: { scopes: string[] },
    axiosList: AxiosInstance[]
): UseAuthenticationResult => {
    const [account, setAccount] = useState<AccountInfo>();
    const [error, setError] = useState<Error>();
    const loggingIn = useRef(false);

    const saveAccount = useCallback(
        async (acct: AccountInfo) => {
            // Acquire a token silently to get a fresh id_token for the Authorization header.
            // We use idToken (not accessToken) because the backend validates tokens with
            // audience = ClientId, which matches the id_token's aud claim.
            try {
                const silentRequest: SilentRequest = {
                    scopes: loginRequest.scopes,
                    account: acct,
                };
                const response = await msalInstance.acquireTokenSilent(
                    silentRequest
                );
                const token = response.idToken || acct.idToken || '';
                axiosList.forEach((axios) => {
                    setAxiosHeader(axios, token);
                });
            } catch {
                // If silent acquisition fails, use cached id token as fallback
                axiosList.forEach((axios) => {
                    setAxiosHeader(axios, acct.idToken ?? '');
                });
            }
            setError(undefined);
            setAccount(acct);
        },
        [msalInstance, loginRequest.scopes, axiosList]
    );

    const login = useCallback(
        async (forceNew = false) => {
            if (loggingIn.current) return;
            loggingIn.current = true;

            // Force-new clears React state and MSAL's active account so the
            // redirect flow starts a fresh interactive login.
            if (forceNew) {
                setAccount(undefined);
                setError(undefined);
                msalInstance.setActiveAccount(null);
            }

            try {
                // Handle redirect promise first (returns null if not returning from redirect)
                const redirectResponse =
                    await msalInstance.handleRedirectPromise();

                if (redirectResponse) {
                    // Returning from a login redirect
                    await saveAccount(redirectResponse.account);

                    const safeRedirectPath = sanitizeRedirectUrl(
                        redirectResponse.state,
                        '/'
                    );
                    window.history.replaceState(
                        null,
                        document.title,
                        safeRedirectPath
                    );
                    return;
                }

                // Check for existing accounts
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0 && !forceNew) {
                    try {
                        const silentRequest: SilentRequest = {
                            scopes: loginRequest.scopes,
                            account: accounts[0],
                        };
                        const response = await msalInstance.acquireTokenSilent(
                            silentRequest
                        );
                        await saveAccount(response.account);
                        return;
                    } catch (silentError) {
                        if (
                            silentError instanceof InteractionRequiredAuthError
                        ) {
                            // Silent acquisition failed, fall through to interactive login
                        } else {
                            setError(new TokenRefreshError());
                            return;
                        }
                    }
                }

                // No cached account or silent failed — start interactive login
                const redirectRequest: RedirectRequest = {
                    scopes: loginRequest.scopes,
                    state: window.location.hash || '/#/',
                };
                await msalInstance.loginRedirect(redirectRequest);
            } catch (e) {
                setError(e instanceof Error ? e : new Error(String(e)));
            } finally {
                loggingIn.current = false;
            }
        },
        [msalInstance, loginRequest.scopes, saveAccount]
    );

    return { account, error, login };
};
